import { PropsWithChildren, ReactElement, useCallback } from "react";

import { useDisclosure, useToast } from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import * as yup from "yup";

import FailureAcknowledge from "components/RequestModals/RequestErrorModals/FailureAcknowledge";
import { selectOptionSchema } from "forms/schemas";
import useUpdateOrder from "hooks/api/orders/useUpdateOrder";
import usePreventNavigation from "hooks/usePreventNavigation";
import {
  BrokenRuleValidationError,
  SchemaValidationError,
} from "services/apiErrors";

const schema = yup.object().shape({
  docEntry: yup.number().required(),
  isDraft: yup.string().required(),
  shipTo: selectOptionSchema.required(),
  poNumber: yup
    .string()
    .optional()
    .max(100, "PO# must be 100 characters or less"),
});

export type EditFormInputs = {
  shipTo: SelectOption;
  poNumber: string | undefined;
  docEntry: number;
  isDraft: string;
};

const EditOrderForm = ({
  children,
  customerCode,
  customerName,
}: PropsWithChildren & {
  customerName: string;
  customerCode: string;
}): ReactElement => {
  const { onOpen, isOpen, onClose } = useDisclosure();

  const toast = useToast();

  const methods = useForm<EditFormInputs>({
    defaultValues: {
      docEntry: undefined,
      isDraft: undefined,
      shipTo: undefined,
      poNumber: undefined,
    },
    resolver: yupResolver(schema),
  });

  usePreventNavigation({
    enabled: methods.formState.isDirty,
    message: "You have unsaved changes. Are you sure you want to leave?",
  });

  const { mutateAsync: updateOrder, error } = useUpdateOrder(customerCode);

  const onSubmit: SubmitHandler<EditFormInputs> = useCallback(
    (data: EditFormInputs) => {
      return updateOrder({
        shipTo: data.shipTo.value,
        poNumber: data.poNumber,
        docEntry: data.docEntry,
        isDraft: data.isDraft === "true",
      })
        .then(() => {
          toast({
            title: "Success!",
            description: "Your order has been updated.",
            status: "success",
            duration: 3000,
            isClosable: true,
            position: "top",
          });
        })
        .catch((err) => {
          if (err instanceof SchemaValidationError) {
            type Edit_Order_Input_Keys = keyof Pick<
              EditFormInputs,
              "shipTo" | "poNumber"
            >;
            const propertyNameToLabelMap: Record<
              Edit_Order_Input_Keys,
              string
            > = {
              shipTo: "Ship To",
              poNumber: "PO#",
            };
            err.errors?.forEach(({ propertyName, message }) => {
              if (
                propertyName &&
                propertyNameToLabelMap.hasOwnProperty(propertyName)
              ) {
                methods.setError(propertyName as Edit_Order_Input_Keys, {
                  message: `${
                    propertyNameToLabelMap[
                      propertyName as Edit_Order_Input_Keys
                    ]
                  } ${message}`,
                  type: "manual",
                });
              }
            });
          }

          if (err) {
            onOpen();
          }
        });
    },
    [methods, onOpen, toast, updateOrder]
  );

  return (
    <FormProvider {...methods}>
      <FailureAcknowledge
        isOpen={isOpen}
        errorMsg={(error as BrokenRuleValidationError)?.Detail}
        onClose={onClose}
        customerName={customerName}
      />
      <form onSubmit={methods.handleSubmit(onSubmit)}>{children}</form>
    </FormProvider>
  );
};

export default EditOrderForm;
